import React, { useState } from "react";
import {
  Card,
  Rank,
  Suit,
  RANKS,
  SUITS,
  SUIT_SYMBOLS,
  RANK_NAMES,
  isRedSuit,
  isSameCard,
} from "./Deck";

interface CardPickerProps {
  /** Cards already selected (to dim them in the picker) */
  usedCards: Card[];
  /** Called when a card is picked */
  onPick: (card: Card) => void;
  /** Called to close the picker */
  onClose: () => void;
}

const CardPicker: React.FC<CardPickerProps> = ({
  usedCards,
  onPick,
  onClose,
}) => {
  const [selectedRank, setSelectedRank] = useState<Rank | null>(null);

  const isUsed = (rank: Rank, suit: Suit) =>
    usedCards.some((c) => isSameCard(c, { rank, suit }));

  const handleRankTap = (rank: Rank) => {
    // If all suits of this rank are used, skip
    const available = SUITS.filter((s) => !isUsed(rank, s));
    if (available.length === 0) return;
    // If only one suit available, auto-select
    if (available.length === 1) {
      onPick({ rank, suit: available[0] });
      setSelectedRank(null);
      return;
    }
    setSelectedRank(rank);
  };

  const handleSuitTap = (suit: Suit) => {
    if (!selectedRank) return;
    if (isUsed(selectedRank, suit)) return;
    onPick({ rank: selectedRank, suit });
    setSelectedRank(null);
  };

  return (
    <div className="bg-gray-800 rounded-xl p-3 border border-gray-700 shadow-xl">
      {/* Suit selector (shown when rank is selected) */}
      {selectedRank && (
        <div className="mb-3">
          <div className="text-gray-400 text-xs mb-1.5 text-center">
            Pick suit for {RANK_NAMES[selectedRank]}
          </div>
          <div className="flex justify-center gap-2">
            {SUITS.map((suit) => {
              const used = isUsed(selectedRank, suit);
              return (
                <button
                  key={suit}
                  onClick={() => handleSuitTap(suit)}
                  disabled={used}
                  className={`w-14 h-14 rounded-lg text-2xl font-bold transition-all
                    ${
                      used
                        ? "bg-gray-700 text-gray-600 cursor-not-allowed"
                        : isRedSuit(suit)
                        ? "bg-gray-700 text-red-500 hover:bg-gray-600 active:scale-95"
                        : "bg-gray-700 text-white hover:bg-gray-600 active:scale-95"
                    }`}
                >
                  {SUIT_SYMBOLS[suit]}
                </button>
              );
            })}
            <button
              onClick={() => setSelectedRank(null)}
              className="w-14 h-14 rounded-lg text-sm text-gray-400 bg-gray-700 hover:bg-gray-600 active:scale-95"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* Rank grid */}
      {!selectedRank && (
        <>
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-400 text-xs">Pick a card</span>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-300 text-sm px-2"
            >
              ✕
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {[...RANKS].reverse().map((rank) => {
              const allUsed = SUITS.every((s) => isUsed(rank, s));
              return (
                <button
                  key={rank}
                  onClick={() => handleRankTap(rank)}
                  disabled={allUsed}
                  className={`h-11 rounded-lg text-sm font-bold transition-all
                    ${
                      allUsed
                        ? "bg-gray-700 text-gray-600 cursor-not-allowed"
                        : "bg-gray-700 text-white hover:bg-gray-600 active:scale-95"
                    }`}
                >
                  {RANK_NAMES[rank]}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default CardPicker;
